import { type FC } from "react";
import { Box, Chip, Divider, Stack, Typography } from "@mui/material";
import { useDateFormat, useTranslation } from "@/shared/hooks";
import { getProductBadge } from "@/shared/utils";
import type { Product } from "@/entities/products";
import { ProductPreviewMedia } from "./ProductPreviewMedia";
import { ProductFeedbackButton } from "./ProductFeedbackButton";

// Левая колонка страницы продукта (макет 1b). Разбита на две секции, потому
// что на мобилке между ними встаёт карточка цены: `top` — обложка с бейджем,
// заголовок и строка метаданных; `description` — разделитель, «О событии»,
// html описания и кнопка обратной связи. Порядок задаёт ProductCover.
export const ProductHero: FC<{
  product: Product;
  section: "top" | "description";
}> = ({ product, section }) => {
  const { t } = useTranslation();
  const { formatDate, formatDateRange } = useDateFormat();
  const badge = getProductBadge(product);
  const badgeLabel = t(`product.badge.${badge.kind}`);

  // Порт core/utils/renderEventDate.js — строка собирается из непустых
  // [eventStartAt, eventFinishAt], поэтому дата показывается и когда задана
  // только дата окончания (без даты начала).
  let eventDate: string | null = null;
  if (product.eventStartAt && product.eventFinishAt) {
    eventDate = formatDateRange(product.eventStartAt, product.eventFinishAt);
  } else if (product.eventStartAt) {
    eventDate = formatDate(product.eventStartAt);
  } else if (product.eventFinishAt) {
    eventDate = formatDate(product.eventFinishAt);
  }

  if (section === "description") {
    // Блок рендерится всегда: даже без описания под ним нужна кнопка
    // обратной связи (референсная «Contact the rightsholder»).
    return (
      <Box>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="label" sx={{ mb: 1.5 }}>
          {t(
            product.type === "SUBSCRIPTION"
              ? "product.aboutSubscription"
              : "product.aboutEvent",
          )}
        </Typography>
        {product.html && (
          <Typography
            component="div"
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: 720,
              "& img": { maxWidth: "100%" },
              "& :first-of-type": { mt: 0 },
              "& :last-of-type": { mb: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: product.html }}
          />
        )}
        <Box sx={{ mt: 2.5 }}>
          <ProductFeedbackButton productId={product.id} />
        </Box>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Box
        sx={{ position: "relative", borderRadius: "14px", overflow: "hidden" }}
      >
        <ProductPreviewMedia product={product} />
        <Chip
          variant="badge"
          label={badgeLabel}
          sx={{ position: "absolute", top: 16, right: 16 }}
        />
      </Box>

      <Box>
        <Typography
          variant="h3"
          component="h1"
          sx={{ textTransform: "uppercase" }}
        >
          {product.title}
        </Typography>
        {/* Строка метаданных — только дата события: тип продукта уже
          написан на бейдже поверх обложки, дублировать его здесь не нужно. */}
        {eventDate && (
          <Typography
            variant="overline"
            sx={{ display: "block", mt: 1.5, color: "text.muted" }}
          >
            {eventDate}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
